import React, { useEffect, useState } from "react";

/**
 * Premium loading state shown while Claude Opus is generating the
 * Value-Based Offer. Designed as an Apple × BonusClub × AI collab:
 *  - aurora glow background (subtle, slowly drifting)
 *  - glassy "document" skeleton mirroring the final offer layout
 *  - shimmer waves moving across each placeholder line
 *  - rotating status pill with live AI-thought messages
 *  - branded footer ("Apple-grade craft · BonusClub Brain")
 *
 * No props required — but `tier` controls the # of option cards (default 3).
 */

const STATUS_MESSAGES = [
  "BonusClub Intelligence liest die Konfiguration ein …",
  "Bewertet Markt, Branche & Wettbewerb …",
  "Modelliert Verkaufswert pro Lead und Lifetime …",
  "Kalibriert Preise an deinen Mindestpreisen …",
  "Verfasst Intro & Situationsüberblick …",
  "Formuliert Ziele und Messgrößen …",
  "Schreibt Werttext & Argumentation …",
  "Baut die drei Pakete (Foundation · Plus · Premium) …",
  "Setzt Termin-Bonus & Erfolgs-Komponente in jedes Paket …",
  "Stimmt Tonalität auf den Decision-Maker ab …",
  "Letzter Feinschliff: Stil, Typografie, Konsistenz …",
];

const Sparkle = ({ size = 14, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2 13.6 9.2 20.8 10.8 13.6 12.4 12 19.6 10.4 12.4 3.2 10.8 10.4 9.2 12 2Z" />
  </svg>
);

const GeneratingSkeleton = () => {
  const [statusIdx, setStatusIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setStatusIdx((i) => (i + 1) % STATUS_MESSAGES.length),
      2400
    );
    return () => clearInterval(t);
  }, []);

  // Estimated remaining seconds — counts down from 75s, never below 5s.
  const [remaining, setRemaining] = useState(75);
  useEffect(() => {
    const t = setInterval(() => {
      setRemaining((r) => (r > 5 ? r - 1 : r));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="vbo-skeleton" role="status" aria-live="polite">
      <div className="vbo-skeleton__aurora" aria-hidden="true" />

      <div className="vbo-skeleton__head">
        <div className="vbo-skeleton__brand">
          <span className="vbo-skeleton__brand-mark">
            <Sparkle size={12} className="vbo-skeleton__brand-spark" />
          </span>
          <span className="vbo-skeleton__brand-text">
            <strong>BonusClub</strong>
            <span className="vbo-skeleton__brand-sep">·</span>
            <span>Intelligence</span>
          </span>
        </div>

        <div className="vbo-skeleton__status">
          <span className="vbo-skeleton__status-dot" aria-hidden="true" />
          <span className="vbo-skeleton__status-text" key={statusIdx}>
            {STATUS_MESSAGES[statusIdx]}
          </span>
          <span className="vbo-skeleton__status-eta">
            ~{remaining} Sek.
          </span>
        </div>

        <div className="vbo-skeleton__progress" aria-hidden="true">
          <div className="vbo-skeleton__progress-bar" />
        </div>
      </div>

      <div className="vbo-skeleton__doc">
        <div className="vbo-skel-eyebrow vbo-skel-shimmer" />
        <div className="vbo-skel-title vbo-skel-shimmer" />
        <div
          className="vbo-skel-line vbo-skel-shimmer"
          style={{ width: "62%" }}
        />

        <div className="vbo-skel-divider" />

        <div className="vbo-skel-section">
          <div className="vbo-skel-section-head">
            <div
              className="vbo-skel-h vbo-skel-shimmer"
              style={{ width: 140 }}
            />
            <div
              className="vbo-skel-pill vbo-skel-shimmer"
              style={{ width: 60 }}
            />
          </div>
          <div
            className="vbo-skel-line vbo-skel-shimmer"
            style={{ width: "94%" }}
          />
          <div
            className="vbo-skel-line vbo-skel-shimmer"
            style={{ width: "88%" }}
          />
          <div
            className="vbo-skel-line vbo-skel-shimmer"
            style={{ width: "73%" }}
          />
        </div>

        <div className="vbo-skel-section">
          <div className="vbo-skel-section-head">
            <div
              className="vbo-skel-h vbo-skel-shimmer"
              style={{ width: 180 }}
            />
          </div>
          <div className="vbo-skel-bullets">
            <div className="vbo-skel-bullet">
              <span className="vbo-skel-dot vbo-skel-shimmer" />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "82%" }}
              />
            </div>
            <div className="vbo-skel-bullet">
              <span className="vbo-skel-dot vbo-skel-shimmer" />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "70%" }}
              />
            </div>
            <div className="vbo-skel-bullet">
              <span className="vbo-skel-dot vbo-skel-shimmer" />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "58%" }}
              />
            </div>
          </div>
        </div>

        <div className="vbo-skel-options">
          {["Foundation", "Plus", "Premium"].map((tier, i) => (
            <div
              key={tier}
              className={`vbo-skel-option ${
                i === 1 ? "vbo-skel-option--accent" : ""
              }`}
            >
              <div className="vbo-skel-option-head">
                <div
                  className="vbo-skel-option-tier vbo-skel-shimmer"
                  style={{ width: 90 }}
                />
                {i === 1 && (
                  <div
                    className="vbo-skel-pill vbo-skel-pill--accent vbo-skel-shimmer"
                    style={{ width: 56 }}
                  />
                )}
              </div>
              <div className="vbo-skel-option-price vbo-skel-shimmer" />
              <div className="vbo-skel-divider vbo-skel-divider--soft" />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "78%" }}
              />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "66%" }}
              />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "54%" }}
              />
              <div
                className="vbo-skel-line vbo-skel-shimmer"
                style={{ width: "70%" }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="vbo-skeleton__footer">
        <Sparkle size={12} className="vbo-skeleton__footer-spark" />
        <span>
          Apple-grade craft <span className="vbo-skeleton__footer-sep">·</span>{" "}
          BonusClub Brain
        </span>
        <Sparkle size={10} className="vbo-skeleton__footer-spark" />
      </div>
    </div>
  );
};

export default GeneratingSkeleton;
